import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Heart, ShoppingBag, Star, Truck, Shield, RotateCcw } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const { addToCart, toggleWishlist, inWishlist } = useCart();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [qty, setQty] = useState(1);
  const [showReview, setShowReview] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: "", body: "" });

  useEffect(() => {
    setData(null); setImgIdx(0); setSize(""); setColor(""); setQty(1);
    api.get(`/products/${id}`).then((r) => {
      setData(r.data);
      setColor(r.data.product.colors?.[0] || "");
    });
  }, [id]);

  if (!data) return (
    <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-12">
      <div className="animate-pulse grid lg:grid-cols-2 gap-10">
        <div className="aspect-[3/4] bg-black/5" />
        <div className="space-y-4">
          <div className="h-8 w-2/3 bg-black/5" />
          <div className="h-6 w-1/3 bg-black/5" />
          <div className="h-32 bg-black/5" />
        </div>
      </div>
    </div>
  );

  const p = data.product;
  const discount = p.mrp > p.price ? Math.round(((p.mrp - p.price) / p.mrp) * 100) : 0;

  const handleAdd = async () => {
    if (!size) { toast.error("Select a size"); return; }
    if (!color) { toast.error("Select a color"); return; }
    await addToCart(p, size, color, qty);
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Sign in to leave a review"); return; }
    try {
      await api.post("/reviews", { product_id: p.id, ...reviewForm });
      toast.success("Review posted");
      setShowReview(false);
      api.get(`/products/${id}`).then((r) => setData(r.data));
    } catch (e) {
      toast.error("Could not post review");
    }
  };

  return (
    <div className="bg-white" data-testid="pdp-page">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-8">
        <div className="text-xs font-mono text-black/40 mb-6">
          <Link to="/" className="hover:text-black">HOME</Link> / <Link to={`/shop/${p.gender}`} className="hover:text-black">{p.gender?.toUpperCase()}</Link> / {p.name.toUpperCase()}
        </div>

        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10">
          {/* Gallery */}
          <div className="grid grid-cols-[80px_1fr] gap-4 items-start">
            <div className="flex flex-col gap-2">
              {p.images?.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setImgIdx(i)}
                  className={`aspect-square border ${imgIdx === i ? "border-black" : "border-black/20"} overflow-hidden`}
                  data-testid={`thumb-${i}`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
            <div className="bg-[#f1f1f1] aspect-[3/4] max-h-[720px] overflow-hidden">
              <img src={p.images?.[imgIdx]} alt={p.name} className="w-full h-full object-cover" />
            </div>
          </div>

          {/* Details */}
          <div className="lg:sticky lg:top-32 lg:self-start space-y-6">
            {p.collab && <span className="inline-block bg-black text-white font-display tracking-widest text-xs px-3 py-1">{p.collab}</span>}
            <div>
              <h1 className="font-display text-5xl tracking-tighter">{p.name.toUpperCase()}</h1>
              {p.rating_count > 0 ? (
                <div className="flex items-center gap-3 mt-2">
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map((n) => (
                      <Star key={n} size={14} className={n <= Math.round(p.rating_avg || 0) ? "fill-[#FF3B30] text-[#FF3B30]" : "text-black/30"} />
                    ))}
                  </div>
                  <span className="text-xs text-black/50 font-mono">{p.rating_count} REVIEW{p.rating_count > 1 ? "S" : ""}</span>
                </div>
              ) : (
                <div className="mt-2 text-xs font-mono text-black/40">BE THE FIRST TO REVIEW</div>
              )}
            </div>

            <div className="flex items-baseline gap-4 border-y border-black/10 py-4">
              <span className="font-display text-4xl">{formatINR(p.price)}</span>
              {p.mrp > p.price && (
                <>
                  <span className="text-black/40 line-through">{formatINR(p.mrp)}</span>
                  <span className="text-[#FF3B30] font-display tracking-widest text-sm">-{discount}% OFF</span>
                </>
              )}
            </div>

            <div>
              <div className="font-display text-xs tracking-widest text-black/50 mb-2">COLOR: <span className="text-black">{color}</span></div>
              <div className="flex gap-2">
                {p.colors?.map((c) => (
                  <button
                    key={c}
                    onClick={() => setColor(c)}
                    data-testid={`color-${c}`}
                    className={`px-4 py-2 text-xs font-display tracking-widest border ${color === c ? "bg-black text-white border-black" : "border-black/30 text-black hover:bg-black hover:text-white"}`}
                  >{c.toUpperCase()}</button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex justify-between items-baseline mb-2">
                <div className="font-display text-xs tracking-widest text-black/50">SIZE</div>
                <button className="text-xs font-mono underline text-black/60" data-testid="size-chart-btn">SIZE CHART</button>
              </div>
              <div className="grid grid-cols-5 gap-2">
                {p.sizes?.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSize(s)}
                    data-testid={`size-${s}`}
                    className={`py-3 text-sm font-display tracking-widest border ${size === s ? "bg-black text-white border-black" : "border-black/30 hover:bg-black hover:text-white"}`}
                  >{s}</button>
                ))}
              </div>
              <p className="text-xs font-mono text-black/40 mt-2">FIT: {p.fit?.toUpperCase()}</p>
            </div>

            {p.stock < 15 && (
              <div className="text-[#FF3B30] font-display text-xs tracking-widest animate-pulse" data-testid="low-stock">
                ⚠ ONLY {p.stock} LEFT — SELLING FAST
              </div>
            )}

            <div className="flex items-center gap-3">
              <div className="flex items-center border border-black/30">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-12 hover-invert" data-testid="qty-minus">−</button>
                <span className="w-12 text-center font-display" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty(Math.min(10, qty + 1))} className="w-10 h-12 hover-invert" data-testid="qty-plus">+</button>
              </div>
              <button
                onClick={handleAdd}
                className="flex-1 bg-[#FF3B30] text-white py-4 font-display tracking-widest text-sm hover:bg-black"
                data-testid="add-to-cart-btn"
              >
                <ShoppingBag size={16} className="inline mr-2" /> ADD TO CART
              </button>
              <button
                onClick={() => toggleWishlist(p)}
                className={`w-12 h-12 border flex items-center justify-center ${inWishlist(p.id) ? "bg-black text-white border-black" : "border-black/30 hover-invert"}`}
                data-testid="wishlist-toggle-btn"
                aria-label="wishlist"
              >
                <Heart size={16} fill={inWishlist(p.id) ? "currentColor" : "none"} />
              </button>
            </div>

            <div className="grid grid-cols-3 gap-3 pt-4 border-t border-black/10">
              <FeatureBlock icon={<Truck size={18} />} title="FREE SHIPPING" desc="ON ORDERS ₹1499+" />
              <FeatureBlock icon={<RotateCcw size={18} />} title="EASY RETURNS" desc="7 DAY POLICY" />
              <FeatureBlock icon={<Shield size={18} />} title="AUTHENTIC" desc="VERIFIED ORIGINAL" />
            </div>

            <div className="border-t border-black/10 pt-6">
              <div className="font-display tracking-widest text-xs text-black/50 mb-3">DESCRIPTION</div>
              <p className="text-black/80 leading-relaxed">{p.description}</p>
            </div>
          </div>
        </div>

        {/* REVIEWS */}
        <section className="mt-24 border-t border-black/10 pt-12">
          <div className="flex items-end justify-between mb-8">
            <h2 className="font-display text-4xl tracking-tighter">REVIEWS ({data.reviews.length})</h2>
            <button onClick={() => setShowReview((s) => !s)} className="font-display tracking-widest text-xs border border-black px-6 py-3 hover-invert" data-testid="write-review-btn">
              WRITE A REVIEW
            </button>
          </div>

          {showReview && (
            <form onSubmit={submitReview} className="border border-black/15 p-6 mb-8 space-y-4">
              <div>
                <div className="font-display tracking-widest text-xs text-black/60 mb-2">RATING</div>
                <div className="flex gap-2">
                  {[1,2,3,4,5].map((n) => (
                    <button key={n} type="button" onClick={() => setReviewForm({ ...reviewForm, rating: n })}>
                      <Star size={24} className={n <= reviewForm.rating ? "fill-[#FF3B30] text-[#FF3B30]" : "text-black/30"} />
                    </button>
                  ))}
                </div>
              </div>
              <input
                value={reviewForm.title}
                onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                placeholder="TITLE"
                className="w-full bg-transparent border border-black/30 px-4 py-3 font-display tracking-widest text-sm"
                required
                data-testid="review-title"
              />
              <textarea
                value={reviewForm.body}
                onChange={(e) => setReviewForm({ ...reviewForm, body: e.target.value })}
                placeholder="What did you love about this product?"
                rows={4}
                className="w-full bg-transparent border border-black/30 px-4 py-3"
                required
                data-testid="review-body"
              />
              <button type="submit" className="bg-black text-white px-8 py-3 font-display tracking-widest text-sm" data-testid="review-submit">SUBMIT REVIEW</button>
            </form>
          )}

          {data.reviews.length === 0 ? (
            <div className="text-black/40 py-12 text-center border border-black/10">No reviews yet. Be the first.</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {data.reviews.map((r) => (
                <div key={r.id} className="border border-black/10 p-6">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-display tracking-widest">{r.user_name.toUpperCase()}</div>
                      <div className="flex gap-0.5 mt-1">
                        {[1,2,3,4,5].map((n) => (
                          <Star key={n} size={12} className={n <= r.rating ? "fill-[#FF3B30] text-[#FF3B30]" : "text-black/30"} />
                        ))}
                      </div>
                    </div>
                    <span className="text-[10px] font-mono text-black/40">{new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="font-semibold mt-2">{r.title}</div>
                  <p className="text-black/70 mt-1 text-sm">{r.body}</p>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* COMPLETE THE LOOK */}
        {data.related?.length > 0 && (
          <section className="mt-24 border-t border-black/10 pt-12">
            <h2 className="font-display text-4xl tracking-tighter mb-8">COMPLETE THE LOOK</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {data.related.map((rp, i) => <ProductCard key={rp.id} product={rp} index={i} />)}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function FeatureBlock({ icon, title, desc }) {
  return (
    <div className="text-center border border-black/10 p-3">
      <div className="flex justify-center mb-2 text-[#FF3B30]">{icon}</div>
      <div className="font-display tracking-widest text-[11px]">{title}</div>
      <div className="text-[10px] font-mono text-black/40 mt-1">{desc}</div>
    </div>
  );
}
